/**
 * useDecideAbandonedBelongings — supervisor / contractor_admin /
 * super_admin mutation that calls decide-abandoned-belongings.
 * Surfaces structured error codes from the RPC as AppError.code for
 * inline blockers (CLAUDE.md ERROR 4).
 *
 * Mirrors useDecideAdditionalFee so contract-layer supervisor
 * decision surfaces stay shaped the same way.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type AbandonedBelongingsDecision = 'acknowledge' | 'mark_removed' | 'mark_billed'

export interface DecideAbandonedBelongingsVars {
  reportId:           string
  decision:           AbandonedBelongingsDecision
  dispositionNotes?:  string
}

export interface DecideAbandonedBelongingsResult {
  report_id: string
  status:    'acknowledged' | 'removed' | 'billed'
}

export function useDecideAbandonedBelongings(): UseMutationResult<DecideAbandonedBelongingsResult, AppError, DecideAbandonedBelongingsVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<DecideAbandonedBelongingsResult, AppError, DecideAbandonedBelongingsVars>({
    mutationFn: async (vars): Promise<DecideAbandonedBelongingsResult> => {
      const trimmed = vars.dispositionNotes?.trim()
      const { data, error } = await supabase.functions.invoke<DecideAbandonedBelongingsResult>('decide-abandoned-belongings', {
        body: {
          report_id:          vars.reportId,
          decision:           vars.decision,
          disposition_notes:  trimmed && trimmed.length > 0 ? trimmed : undefined,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'DECIDE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Decision returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['abandoned-belongings'] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_DECISION':   return 'That decision is not allowed.'
    case 'INVALID_STATE':      return 'This report is not in a state where that decision can be applied.'
    case 'INVALID_NOTES':      return 'Disposition notes must be 1–1,000 characters when provided.'
    case 'FORBIDDEN_ROLE':     return 'Your role is not allowed to decide abandoned-belongings reports.'
    case 'FORBIDDEN_ORG':      return 'This report does not belong to your organization.'
    case 'REPORT_NOT_FOUND':   return 'This report could not be found.'
    case 'RATE_LIMITED':       return 'Too many decisions in a row. Try again in a moment.'
    default:                   return detail || 'Could not record the decision.'
  }
}
